/**
 * Amount-in-words (تفقيط) for printed documents — the cheque-style spelled-out
 * total that sits beside the numeric grand total on invoices, bills, orders,
 * quotations and journal vouchers.
 *
 * Two languages:
 *  - English: classic "Three thousand three hundred thirty-five US Dollars only".
 *  - Arabic:  REAL words like a bank cheque, e.g. 3335 IQD →
 *      «ثلاثة آلاف وثلاثمائة وخمسة وثلاثون ديناراً عراقياً فقط»
 *    (proper ones/tens/teens/hundreds/thousands/millions with «و» separators,
 *     grammatical counted-noun forms, and a trailing «فقط»).
 *
 * Currency decimals decide whether a fractional (minor-unit) part is spelled:
 * IQD is 0-decimals → no «سنت»; USD is 2-decimals → «... وخمسون سنتاً». The
 * decimal count comes from the backend `currencies.exponent` registry at
 * runtime (money.ts), falling back to a small built-in table so the util is
 * self-contained (and unit-testable without the app booted).
 *
 * Case-endings (إعراب) are the common accounting forms, not full classical
 * vocalisation — accusative tamyiz «ديناراً» for 11–99, plurals «دنانير» /
 * «آلاف» for 3–10, dual «ديناران» for 2, singular otherwise.
 */

import { getCurrencyDecimals } from './money.js';

/* ---------------------------------------------------------------- English --- */

const EN_ONES = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
  'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const EN_TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function enWords3(n: number): string {
  let s = '';
  if (n >= 100) { s += `${EN_ONES[Math.floor(n / 100)]} hundred`; n %= 100; if (n) s += ' '; }
  if (n >= 20) { s += EN_TENS[Math.floor(n / 10)]; n %= 10; if (n) s += `-${EN_ONES[n]}`; }
  else if (n) s += EN_ONES[n];
  return s;
}

/** Spell a non-negative integer in English words ("three thousand …"). */
export function toWordsEN(n: number): string {
  n = Math.floor(n);
  if (n === 0) return 'zero';
  const scale: [string, number][] = [['billion', 1e9], ['million', 1e6], ['thousand', 1e3]];
  let s = '';
  for (const [w, v] of scale) { if (n >= v) { s += `${enWords3(Math.floor(n / v))} ${w}`; n %= v; if (n) s += ' '; } }
  if (n) s += enWords3(n);
  return s;
}

/* ----------------------------------------------------------------- Arabic --- */

// Masculine forms — our currency nouns (دينار، دولار، سنت …) are all masculine,
// so 3–10 carry the «ة» (gender-polarity rule) and the teens follow suit.
const AR_ONES = ['', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة', 'عشرة',
  'أحد عشر', 'اثنا عشر', 'ثلاثة عشر', 'أربعة عشر', 'خمسة عشر', 'ستة عشر', 'سبعة عشر', 'ثمانية عشر', 'تسعة عشر'];
const AR_TENS = ['', '', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'];
const AR_HUNDREDS = ['', 'مائة', 'مئتان', 'ثلاثمائة', 'أربعمائة', 'خمسمائة', 'ستمائة', 'سبعمائة', 'ثمانمائة', 'تسعمائة'];

/** Words for a 0–999 group (ones before tens, joined with «و»). */
function arWords3(n: number): string {
  const parts: string[] = [];
  const h = Math.floor(n / 100);
  const rem = n % 100;
  if (h) parts.push(AR_HUNDREDS[h]!);
  if (rem) {
    if (rem < 20) parts.push(AR_ONES[rem]!);
    else {
      const tens = Math.floor(rem / 10);
      const ones = rem % 10;
      parts.push(ones ? `${AR_ONES[ones]} و${AR_TENS[tens]}` : AR_TENS[tens]!);
    }
  }
  return parts.join(' و');
}

interface ArScale { limit: number; one: string; two: string; few: string; many: string; }
// «مليون/مليونان/ملايين» then «ألف/ألفان/آلاف». `few` = 3–10, `many` = 11+.
const AR_SCALES: ArScale[] = [
  { limit: 1e6, one: 'مليون', two: 'مليونان', few: 'ملايين', many: 'مليون' },
  { limit: 1e3, one: 'ألف', two: 'ألفان', few: 'آلاف', many: 'ألف' },
];

/** Spell a non-negative integer in Arabic words («ثلاثة آلاف وثلاثمائة …»). */
export function toWordsAR(n: number): string {
  n = Math.floor(n);
  if (n === 0) return 'صفر';
  const parts: string[] = [];
  let rem = n;
  for (const s of AR_SCALES) {
    const count = Math.floor(rem / s.limit);
    if (count > 0) {
      if (count === 1) parts.push(s.one);
      else if (count === 2) parts.push(s.two);
      else if (count <= 10) parts.push(`${arWords3(count)} ${s.few}`);
      else parts.push(`${arWords3(count)} ${s.many}`);
      rem %= s.limit;
    }
  }
  if (rem > 0) parts.push(arWords3(rem));
  return parts.join(' و');
}

/** Accusative (tanwīn fatḥ) form of a word ending in a bare consonant:
 *  دينار→ديناراً، سنت→سنتاً، عراقي→عراقياً. Left as-is when it already ends in a
 *  long vowel / tāʼ marbūṭa where the suffix would be wrong. */
function arAccusative(w: string): string {
  const last = w[w.length - 1] ?? '';
  if (['ة', 'ا', 'و', 'ى', 'ء', 'ً'].includes(last)) return w;
  return `${w}اً`;
}

interface ArNoun { singular: string; dual: string; plural: string; adj?: string }

/** Dual form of the qualifier adjective: أمريكي→أمريكيان، تركية→تركيتان. */
function arDualAdj(w: string): string {
  return w.endsWith('ة') ? `${w.slice(0, -1)}تان` : `${w}ان`;
}

/** The grammatically-agreeing counted noun («ديناراً عراقياً», «دنانير عراقية»…)
 *  for a given count, with the country/qualifier adjective appended. */
function arCountedNoun(forms: ArNoun, count: number): string {
  const tail = count % 100;
  let noun: string;
  let acc = false;
  let dual = false;
  if (count === 1) noun = forms.singular;
  else if (count === 2) { noun = forms.dual; dual = true; }
  else if (tail >= 3 && tail <= 10) noun = forms.plural;
  else if (tail >= 11 && tail <= 99) { noun = arAccusative(forms.singular); acc = true; }
  else noun = forms.singular; // round hundreds/thousands (…00) or …01
  if (forms.adj) noun += ` ${dual ? arDualAdj(forms.adj) : acc ? arAccusative(forms.adj) : forms.adj}`;
  return noun;
}

/* --------------------------------------------------------------- Currency --- */

interface CurrencyData {
  decimals: number;
  enMajor: string;   // English major-unit noun (plural form used for all counts)
  enMinor: string;   // English minor-unit noun
  major: ArNoun;     // Arabic major unit
  minor?: ArNoun;    // Arabic minor unit (omit for 0-decimal currencies)
}

const CURRENCY_DATA: Record<string, CurrencyData> = {
  IQD: {
    decimals: 0,
    enMajor: 'Iraqi Dinars', enMinor: 'Fils',
    major: { singular: 'دينار', dual: 'ديناران', plural: 'دنانير', adj: 'عراقي' },
  },
  USD: {
    decimals: 2,
    enMajor: 'US Dollars', enMinor: 'Cents',
    major: { singular: 'دولار', dual: 'دولاران', plural: 'دولارات', adj: 'أمريكي' },
    minor: { singular: 'سنت', dual: 'سنتان', plural: 'سنتات' },
  },
  EUR: {
    decimals: 2,
    enMajor: 'Euros', enMinor: 'Cents',
    major: { singular: 'يورو', dual: 'يورو', plural: 'يورو' },
    minor: { singular: 'سنت', dual: 'سنتان', plural: 'سنتات' },
  },
  GBP: {
    decimals: 2,
    enMajor: 'Pounds', enMinor: 'Pence',
    major: { singular: 'جنيه', dual: 'جنيهان', plural: 'جنيهات', adj: 'إسترليني' },
    minor: { singular: 'بنس', dual: 'بنسان', plural: 'بنسات' },
  },
  SAR: {
    decimals: 2,
    enMajor: 'Saudi Riyals', enMinor: 'Halalas',
    major: { singular: 'ريال', dual: 'ريالان', plural: 'ريالات', adj: 'سعودي' },
    minor: { singular: 'هللة', dual: 'هللتان', plural: 'هللات' },
  },
  AED: {
    decimals: 2,
    enMajor: 'Dirhams', enMinor: 'Fils',
    major: { singular: 'درهم', dual: 'درهمان', plural: 'دراهم', adj: 'إماراتي' },
    minor: { singular: 'فلس', dual: 'فلسان', plural: 'فلوس' },
  },
  EGP: {
    decimals: 2,
    enMajor: 'Egyptian Pounds', enMinor: 'Piastres',
    major: { singular: 'جنيه', dual: 'جنيهان', plural: 'جنيهات', adj: 'مصري' },
    minor: { singular: 'قرش', dual: 'قرشان', plural: 'قروش' },
  },
  TRY: {
    decimals: 2,
    enMajor: 'Turkish Liras', enMinor: 'Kurus',
    major: { singular: 'ليرة', dual: 'ليرتان', plural: 'ليرات', adj: 'تركية' },
    minor: { singular: 'قرش', dual: 'قرشان', plural: 'قروش' },
  },
};

/** Fallback Arabic noun for an unknown ISO code — just repeats the code. */
function genericMajor(code: string): ArNoun {
  return { singular: code, dual: code, plural: code };
}

/** Resolve display decimals: prefer the live backend registry, then our built-in
 *  table, then a sane default of 2. */
function resolveDecimals(code: string, data: CurrencyData | undefined): number {
  const registered = getCurrencyDecimals(code);
  if (registered !== undefined) return registered;
  return data?.decimals ?? 2;
}

/**
 * Spell a currency amount in words for the given locale.
 *
 * @param amount        numeric total (from the API string, already parsed)
 * @param currencyCode  ISO code, e.g. "IQD" or "USD"
 * @param locale        active i18n locale; Arabic when it starts with "ar"
 */
export function amountInWords(amount: number, currencyCode: string, locale: string): string {
  const code = (currencyCode || '').toUpperCase();
  const data = CURRENCY_DATA[code];
  const decimals = resolveDecimals(code, data);
  const abs = Math.abs(amount);
  const whole = Math.floor(abs);
  const frac = decimals > 0 ? Math.round((abs - whole) * 10 ** decimals) : 0;
  const isAr = (locale || '').toLowerCase().startsWith('ar');

  if (isAr) {
    const majorForms = data?.major ?? genericMajor(code);
    // One and two follow the counted noun's own grammar, not a number word:
    // «دينار عراقي واحد» and the bare dual «دولاران أمريكيان» — a spelled
    // «اثنان دولاران» is exactly the mistake a bank teller would circle.
    const phrase = (forms: ArNoun, count: number): string => {
      if (count === 1) return `${arCountedNoun(forms, 1)} واحد`;
      if (count === 2) return arCountedNoun(forms, 2);
      return `${toWordsAR(count)} ${arCountedNoun(forms, count)}`;
    };
    const parts: string[] = [];
    if (whole > 0 || frac === 0) parts.push(phrase(majorForms, whole));
    if (frac > 0 && data?.minor) {
      const p = phrase(data.minor, frac);
      parts.push(parts.length ? `و${p}` : p);
    }
    return `${parts.join(' ')} فقط`;
  }

  const major = data?.enMajor ?? code;
  let head = toWordsEN(whole);
  head = head.charAt(0).toUpperCase() + head.slice(1);
  let out = `${head} ${major}`;
  if (frac > 0) {
    const minor = data?.enMinor;
    out += minor ? ` and ${toWordsEN(frac)} ${minor}` : ` and ${frac}/${10 ** decimals}`;
  }
  return `${out} only`;
}
